import type { LoginRequest } from "@beacon/contracts";

export interface SessionProblem {
  readonly code: string;
  readonly detail: string;
  readonly fields?: readonly string[];
  readonly reason?: string;
}

export class SessionApiError extends Error {
  constructor(readonly status: number, readonly problem: SessionProblem) {
    super(problem.detail);
  }
}

async function bodyOrProblem(response: Response): Promise<Record<string, unknown>> {
  const body = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) throw new SessionApiError(response.status, {
    code: typeof body.code === "string" ? body.code : "authentication_unavailable",
    detail: typeof body.detail === "string" ? body.detail : "Não foi possível concluir.",
    ...(Array.isArray(body.fields) && body.fields.every((field) => typeof field === "string") ? { fields: body.fields as string[] } : {}),
    ...(typeof body.reason === "string" ? { reason: body.reason } : {}),
  });
  return body;
}

export async function login(apiUrl: string, request: LoginRequest): Promise<{ next: string }> {
  const response = await fetch(`${apiUrl}/api/v1/session/login`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(request),
  });
  const body = await bodyOrProblem(response);
  if (body.authenticated !== true || typeof body.next !== "string" || !body.next.startsWith("/") || body.next.startsWith("//")) {
    throw new SessionApiError(502, { code: "invalid_response", detail: "O servidor retornou uma confirmação inválida." });
  }
  return { next: body.next };
}

export type OnboardingState = "pending_email_verification" | "pending_phone_verification" | "pending_whatsapp_decision" | "complete";

export async function currentSession(apiUrl: string): Promise<{ expiresAt: string; capabilities: readonly string[]; onboardingState: OnboardingState; next: string; legalRenewalRequired: boolean }> {
  const response = await fetch(`${apiUrl}/api/v1/session`, { credentials: "include", headers: { accept: "application/json" } });
  const body = await bodyOrProblem(response);
  if (body.authenticated !== true || typeof body.expiresAt !== "string" || !Array.isArray(body.capabilities) || !body.capabilities.every((item) => typeof item === "string") || !["pending_email_verification", "pending_phone_verification", "pending_whatsapp_decision", "complete"].includes(String(body.onboardingState)) || typeof body.next !== "string" || typeof body.legalRenewalRequired !== "boolean") throw new SessionApiError(502, { code: "invalid_response", detail: "A sessão retornada é inválida." });
  return { expiresAt: body.expiresAt, capabilities: body.capabilities as string[], onboardingState: body.onboardingState as OnboardingState, next: body.next, legalRenewalRequired: body.legalRenewalRequired };
}

export async function logout(apiUrl: string): Promise<void> {
  const response = await fetch(`${apiUrl}/api/v1/session/logout`, {
    method: "POST",
    credentials: "include",
    headers: { accept: "application/json" },
  });
  await bodyOrProblem(response);
}

export async function acceptLegalDocuments(apiUrl: string, acceptedDocumentIds: readonly string[]): Promise<void> {
  const response = await fetch(`${apiUrl}/api/v1/session/legal-acceptance`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ acceptedDocumentIds }),
  });
  await bodyOrProblem(response);
}
